import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getContentFontStyle, getHeadingFontStyle } from '../../lib/contentBlockFontSize';
import {
  getThemeColorCssValue,
  getThemeColorRawValue,
  isDarkColor,
  normalizeColorValue,
} from '../../lib/themeColors';
import { getIconFallbackLabel } from '../../lib/uiText';
import styles from './DescriptionHome.module.css';

const VALID_TEXT_ALIGN = new Set(['left', 'center', 'right']);
const DESCRIPTION_HOME_FONT_SIZE_FALLBACKS = {
  heading: '1.5rem',
  paragraph: '1rem',
  list: '1rem',
};

const DescriptionHome = ({ data, anchorId }) => {
  const sectionRef = useRef(null);
  const overlayRef = useRef(null);
  const containerRef = useRef(null);
  const hasAnimatedRef = useRef(false);
  const { locale, themeConfig } = useGlobal();
  const contentBlocks = Array.isArray(data?.content) ? data.content.filter(Boolean) : [];

  const backgroundImageMedia = getStrapiMediaAttributes(data?.backgroundImage, {
    altFallback: data?.title || '',
    titleFallback: data?.title || '',
  });
  const backgroundImage = backgroundImageMedia.src;
  const iconMedia = data?.icon?.url
    ? getStrapiMediaAttributes(data.icon, {
      altFallback: getIconFallbackLabel(locale, 'section'),
      titleFallback: getIconFallbackLabel(locale, 'section'),
    })
    : null;
  const hasBackgroundImage = Boolean(backgroundImage);
  const themeColor = normalizeColorValue(data?.themeColor);
  const themeBackgroundColor = getThemeColorRawValue(themeColor, themeConfig);
  const cssThemeBackgroundColor = getThemeColorCssValue(themeColor);
  const hasCardBackground = Boolean(cssThemeBackgroundColor);
  const textAlign = VALID_TEXT_ALIGN.has(data?.textAlign) ? data.textAlign : 'left';
  const useBoldText = Boolean(data?.useBoldText);
  const useLightText = hasBackgroundImage || (hasCardBackground && isDarkColor(themeBackgroundColor));

  const sectionClassName = [
    styles.descriptionHome,
    hasBackgroundImage
      ? styles['descriptionHome--image']
      : hasCardBackground
        ? styles['descriptionHome--card']
        : styles['descriptionHome--transparent'],
    hasBackgroundImage ? styles['descriptionHome--full'] : null,
    styles[`descriptionHome--align-${textAlign}`],
    useLightText ? styles['descriptionHome--light'] : styles['descriptionHome--dark'],
    useBoldText ? styles['descriptionHome--bold'] : null,
  ].filter(Boolean).join(' ');

  const sectionStyle = {};

  if (hasBackgroundImage) {
    sectionStyle.backgroundImage = `url(${backgroundImage})`;
  } else if (hasCardBackground) {
    sectionStyle.backgroundColor = cssThemeBackgroundColor;
  }

  const renderContentBlock = (block, index) => {
    if (!block) return null;

    switch (block.__component) {
      case 'shared.heading': {
        const level = block.level || 'h2';
        const Tag = level;
        const headingStyle = getHeadingFontStyle(block?.fontSize, level, {
          default: DESCRIPTION_HOME_FONT_SIZE_FALLBACKS.heading,
        });
        const headingClassName = [
          styles.descriptionHome__heading,
          styles[`descriptionHome__heading--${level}`],
          styles.descriptionHome__animateItem,
        ].filter(Boolean).join(' ');

        return (
          <Tag key={`${level}-${index}`} className={headingClassName} style={headingStyle}>
            {block.text}
          </Tag>
        );
      }
      case 'shared.paragraph': {
        const paragraphStyle = getContentFontStyle(
          block?.fontSize,
          DESCRIPTION_HOME_FONT_SIZE_FALLBACKS.paragraph
        );
        return (
          <p
            key={`paragraph-${index}`}
            className={[styles.descriptionHome__text, styles.descriptionHome__animateItem].join(' ')}
            style={paragraphStyle}
          >
            {block.text}
          </p>
        );
      }
      case 'shared.list': {
        const listItemStyle = getContentFontStyle(block?.fontSize, DESCRIPTION_HOME_FONT_SIZE_FALLBACKS.list);
        const hasIcon = block.icon?.url;
        const listIconMedia = hasIcon
          ? getStrapiMediaAttributes(block.icon, {
            altFallback: getIconFallbackLabel(locale, 'list'),
            titleFallback: getIconFallbackLabel(locale, 'list'),
          })
          : null;
        const ListTag = !hasIcon && block.ordered ? 'ol' : 'ul';
        const listClassName = [
          styles.descriptionHome__list,
          hasIcon ? styles['descriptionHome__list--icons'] : null,
          !hasIcon && block.ordered ? styles['descriptionHome__list--ordered'] : null,
        ].filter(Boolean).join(' ');

        return (
          <ListTag key={`list-${index}`} className={listClassName}>
            {block.items?.map((item, itemIndex) => (
              <li
                key={`list-item-${itemIndex}`}
                className={[styles.descriptionHome__listItem, styles.descriptionHome__animateItem].join(' ')}
                style={listItemStyle}
              >
                {listIconMedia?.src && (
                  <img
                    className={styles.descriptionHome__listIcon}
                    src={listIconMedia.src}
                    alt={listIconMedia.alt}
                    title={listIconMedia.title}
                    aria-hidden="true"
                  />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ListTag>
        );
      }
      case 'shared.image': {
        const contentImageMedia = getStrapiMediaAttributes(block.image, {
          altFallback: block.alt || '',
          titleFallback: block.alt || '',
        });
        if (!contentImageMedia.src) return null;
        return (
          <img
            key={`image-${index}`}
            className={[styles.descriptionHome__contentImage, styles.descriptionHome__animateItem].join(' ')}
            src={contentImageMedia.src}
            alt={contentImageMedia.alt}
            title={contentImageMedia.title}
          />
        );
      }
      default:
        return null;
    }
  };

  useEffect(() => {
    if (hasBackgroundImage) return;

    const sectionEl = sectionRef.current;
    const contentEl = containerRef.current;
    if (!sectionEl || !contentEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const headingEls = contentEl.querySelectorAll(`.${styles.descriptionHome__heading}`);
    const otherEls = contentEl.querySelectorAll(
      `.${styles.descriptionHome__animateItem}:not(.${styles.descriptionHome__heading})`
    );
    const iconEl = contentEl.querySelector(`.${styles.descriptionHome__icon}`);

    gsap.set(sectionEl, { opacity: 0, y: 50 });
    gsap.set([...headingEls], { opacity: 0, y: 20 });
    gsap.set([...otherEls], { opacity: 0, y: 20 });
    if (iconEl) gsap.set(iconEl, { opacity: 0, y: 20 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;
          hasAnimatedRef.current = true;

          const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

          timeline.to(sectionEl, { opacity: 1, y: 0, duration: 0.6 });

          if (iconEl) {
            timeline.to(iconEl, { opacity: 1, y: 0, duration: 0.4 }, '-=0.2');
          }

          if (headingEls.length) {
            timeline.to(headingEls, { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }, '-=0.2');
          }

          if (otherEls.length) {
            timeline.to(otherEls, { opacity: 1, y: 0, duration: 0.5, stagger: 0.08 }, '-=0.15');
          }

          observer.disconnect();
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(sectionEl);
    return () => observer.disconnect();
  }, [hasBackgroundImage]);

  useEffect(() => {
    if (!hasBackgroundImage) return;

    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      if (overlayRef.current) {
        gsap.set(overlayRef.current, { opacity: 0.7 });
      }
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;

          hasAnimatedRef.current = true;

          const overlayEl = overlayRef.current;
          const contentEl = containerRef.current;
          const headingEls = contentEl?.querySelectorAll(`.${styles.descriptionHome__heading}`) || [];
          const itemEls = contentEl?.querySelectorAll(`.${styles.descriptionHome__animateItem}`) || [];
          const restEls = Array.from(itemEls).filter(
            (el) => !el.classList.contains(styles.descriptionHome__heading)
          );

          gsap.set([...headingEls, ...restEls], { opacity: 0, y: 24 });
          if (overlayEl) gsap.set(overlayEl, { opacity: 0 });

          const timeline = gsap.timeline({ defaults: { duration: 0.6, ease: 'power2.out' } });

          if (overlayEl) {
            timeline.to(overlayEl, { opacity: 0.7, duration: 0.5 });
          }

          if (headingEls.length) {
            timeline.to(headingEls, { opacity: 1, y: 0, stagger: 0.12 }, '-=0.1');
          }

          if (restEls.length) {
            timeline.to(restEls, { opacity: 1, y: 0, stagger: 0.1 }, '-=0.05');
          }

          observer.disconnect();
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(sectionEl);

    return () => observer.disconnect();
  }, [hasBackgroundImage]);

  useEffect(() => {
    if (!hasBackgroundImage) return;

    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    let frameId = null;

    const updateParallax = () => {
      frameId = null;
      const rect = sectionEl.getBoundingClientRect();
      const viewHeight = window.innerHeight || 0;
      const progress = Math.min(Math.max((viewHeight - rect.top) / (viewHeight + rect.height), 0), 1);
      const offset = Math.round((progress - 0.5) * 60);
      sectionEl.style.setProperty('--description-home-bg-y', `${offset}px`);
    };

    const onScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(updateParallax);
    };

    updateParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [hasBackgroundImage]);

  if (!data) return null;

  return (
    <section
      ref={sectionRef}
      id={anchorId || undefined}
      className={sectionClassName}
      style={sectionStyle}
      title={hasBackgroundImage ? backgroundImageMedia.title : undefined}
    >
      {hasBackgroundImage && <div ref={overlayRef} className={styles.descriptionHome__overlay} />}
      <div ref={containerRef} className={styles.descriptionHome__container}>
        {iconMedia?.src && (
          <img
            className={[styles.descriptionHome__icon, styles.descriptionHome__animateItem].join(' ')}
            src={iconMedia.src}
            alt={iconMedia.alt}
            title={iconMedia.title}
            aria-hidden="true"
          />
        )}
        <div className={styles.descriptionHome__content}>
          {contentBlocks.map((block, index) => renderContentBlock(block, index))}
        </div>
      </div>
    </section>
  );
};

export default DescriptionHome;